import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { UsersService } from '../services';
import { usersKeys } from '../queryKeys';
import { INTERNAL_USERS_DEFAULT_LIMIT } from '../clients/GetInternalUsers';

export const GOTU_ADMINS_PAGE_SIZE = INTERNAL_USERS_DEFAULT_LIMIT;

/**
 * A page of GoTu Admins for the list page.
 *
 * pageSize is fixed at the endpoint's default rather than exposed: the story
 * specifies 15 and nothing else asks for a different size.
 *
 * @param {Object} [params]
 * @param {number} [params.page=1] - 1-based page.
 * @param {string} [params.search=''] - Matches name or email.
 * @param {string[]} [params.roles=[]] - Role slugs; empty means every role.
 */
export const useGetInternalUsers = ({ page = 1, search = '', roles = [] } = {}) => {
  const filters = { search, roles };

  const { data, error, isError, isPending, isFetching } = useQuery({
    queryKey: usersKeys.internalUsers(filters, page),
    queryFn: () =>
      UsersService.getInternalUsers({
        page,
        limit: GOTU_ADMINS_PAGE_SIZE,
        search,
        roles,
      }),
    retry: false,
    // Keeps the current page on screen while the next one loads, so paging and
    // typing in the search box do not blank the table.
    placeholderData: keepPreviousData,
  });

  return {
    admins: data?.data ?? [],
    // Nullish while unknown, never 0: useClampedPage reads a 0 as "one page"
    // and would reset the admin's page to 1 on top of a failed request.
    totalCount: data?.total ?? null,
    pageSize: GOTU_ADMINS_PAGE_SIZE,
    isPending,
    isFetching,
    isError,
    error,
  };
};
